import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { RequestFailureAlert } from './RequestFailureAlert.tsx'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'
import { BSViewIndicators } from '@woo-ext/ui'
import '@woo-ext/styles/chrome-extension.scss'
import { AuthLayout } from './routes/AuthLayout.tsx'
import { ExtensionLayout } from './routes/ExtensionLayout.tsx'
import { dialogRoutes, FullPageDialogLayout } from './routes/dialog.tsx'
import { extensionRoutes } from './routes/extension.tsx'
import { NotificationsFrame } from './routes/NotificationsFrame.tsx'
import { standaloneRoutes } from './routes/standalone.tsx'
import { authRoutes, otherwiseRedirect } from './routes/auth.tsx'
import { installAuthStateListener } from './authStateListener.ts'
import { installMouseflow } from './mouseflow.ts'
import { installPageTracking } from './pageTracking.ts'
import { installShortlistSession } from './shortlistSession.ts'
import { BootstrapDebugPage, rootLoader, RootErrorBoundary } from './routes/root.tsx'

// HTML5 mode with `<base href="/">` upstream (§1.3), so paths are real paths — no hash
// routing. The route paths here are the contract careero's BI importers prefix-match
// (§1.3), which is why they come from routes.json rather than being retyped.
const router = createBrowserRouter([
  {
    path: '/chrome-extension',
    loader: rootLoader,
    errorElement: <RootErrorBoundary />,
    element: <BootstrapDebugPage />,
  },
  {
    // The auth branch (§1.3). Nested so every screen shares one path prefix, exactly as
    // ui-router nested them under `chrome-extension.auth`.
    path: '/chrome-extension/auth',
    errorElement: <RootErrorBoundary />,
    element: <AuthLayout />,
    children: authRoutes,
  },
  {
    // The extension branch (§1.3, plan §7 phase 3). Abstract upstream, so it renders chrome
    // and an outlet and claims no path of its own — `/chrome-extension/extension` with no
    // child falls through to `otherwise`, exactly as it does there.
    path: '/chrome-extension/extension',
    errorElement: <RootErrorBoundary />,
    element: <ExtensionLayout />,
    children: extensionRoutes,
  },
  {
    // The full-page dialogs (§1.3, plan §7 phase 5). Abstract upstream with `url: ''`, so it
    // claims no path of its own and every child names a full path — which is why these are
    // listed as children of a pathless route rather than under a shared prefix.
    errorElement: <RootErrorBoundary />,
    element: <FullPageDialogLayout />,
    children: dialogRoutes,
  },
  // `chrome-extension.post` and `chrome-extension.my-account` — off the root abstract state,
  // inside none of the three layouts. See routes/standalone.tsx.
  ...standaloneRoutes.map((route) => ({ ...route, errorElement: <RootErrorBoundary /> })),
  {
    // The shell's second iframe. Empty by design — see NotificationsFrame — and routed so it
    // stays empty instead of falling to the catch-all's "Not ported yet" page.
    path: '/chrome-extension/notifications',
    errorElement: <RootErrorBoundary />,
    element: <NotificationsFrame />,
  },
  {
    // Upstream's `$urlRouterProvider.otherwise` (§1.3) now runs for real — see
    // routes/auth.tsx. It only fires for paths no route claims; a path a later phase owes
    // still renders its own gap page rather than bouncing the user to login.
    path: '*',
    loader: otherwiseRedirect,
    element: (
      <main style={{ font: '14px/1.5 system-ui, sans-serif', padding: '2rem' }}>
        <h1>Not ported yet</h1>
        <p>
          Phase 1 serves <code>/chrome-extension</code> only. See{' '}
          <code>apps/spa/src/routes/manifest.ts</code> for which phase owes this path.
        </p>
      </main>
    ),
  },
])

// Upstream injects `extensionIframeUtilsListenerService` into its root component so that
// every page is listening before any screen renders. Installed on the ROUTER here, and
// outside React, for the reason given in the module: five top-level branches, five
// elements, and no component common to all of them. See authStateListener.ts.
installAuthStateListener(router)

// Session recording (§11 q4 — KEPT, 15 Oct). Installed here for the same reason as the line
// above: upstream put it in the page shell, so it has to cover all five route branches, and no
// component is common to them. See mouseflow.ts for what it records and why that matters.
installMouseflow()

// The shell's session host and the panel's open state — upstream's `ShortlistSessionInfoService`
// and `ShortlistStatusService`, both started at boot. Before the page tracking, which reads both.
installShortlistSession()

// angulartics' virtual page views (`category: 'pageview'`). On the ROUTER, for the reasons in
// pageTracking.ts: every branch is covered, and a loader redirect is tracked once, at its
// destination.
installPageTracking(router)

const root = document.getElementById('root')
if (root === null) throw new Error('#root is missing from index.html')

createRoot(root).render(
  <StrictMode>
    <RouterProvider router={router} />
    {/* Upstream's `HttpErrorInterceptor` is registered app-wide, so its alert is a sibling of
        the router too: it has to cover every route branch. See RequestFailureAlert.tsx. */}
    <RequestFailureAlert />
    {/* ⚠️ A SIBLING OF THE ROUTER, NOT INSIDE A LAYOUT. `chrome-extension.jsp:342` puts
        `<bs-view-indicators>` directly under `<body>`, beside `<ui-view class="main">` — it
        has to outlive every route change, because the detector queries `.device-*` by
        document selector and a probe that unmounts with a layout answers `undefined` on the
        screens that do not render that layout. */}
    <BSViewIndicators />
  </StrictMode>,
)
